import React, { Suspense, lazy, useEffect, startTransition } from 'react';
import { BrowserRouter as Router, Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import { i18n } from './i18n';
import { getLocalizedSlug } from './lib/slugs';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import CustomNavbar from './Components/Navbar/CustomNavbar';
import MobileNavbar from './Components/Navbar/MobileNavbar';
// Not lazy: it is tiny, needed on every page, and suspending it would show the
// app-wide spinner while the head tags loaded.
import PageMeta from './Components/PageMeta/PageMeta';
import HfFooter from './Components/HfFooter/HfFooter';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ExitIntentPopup from './Components/ExitIntentPopup/ExitIntentPopup';
import CookieBanner from './Components/CookieBanner/CookieBanner';
import { trackPageView } from './utils/trackingHelper';
import TermsAndConditions from './Pages/TermsAndConditions';

// Lazy load the components
const ES_MainNavbarMenu = lazy(() => import('./Components/Navbar/ES_MainNavbarMenu'));
const MainNavbarMenu = lazy(() => import('./Components/Navbar/MainNavbarMenu'));
const ProductsPage = lazy(() => import('./Pages/ProductsPage'));
const HansaFlexGroupPage = lazy(() => import('./Pages/HansaFlexGroupPages/HansaFlexGroupPage'));
const HomePage = lazy(() => import('./Pages/HomePage'));
const Management = lazy(() => import('./Pages/HansaFlexSpainPages/Management'));
const WhoWeAre = lazy(() => import('./Pages/HansaFlexSpainPages/WhoWeAre'));
const ContactUs = lazy(() => import('./Components/Contact/contact_us'));
const Certificates = lazy(() => import('./Pages/HansaFlexSpainPages/Certificates'));
const Contact = lazy(() => import('./Components/Contact/Contact'));
const Careers = lazy(() => import('./Pages/Careers'));
const CurrentVacanciew = lazy(() => import('./Pages/CurrentVacanciew'));

const NewsEventsPage = lazy(() => import('./Pages/NewsAndEvents/NewsEventsPage'));
const BlogPedidoUrgente = lazy(() => import('./Pages/NewsAndEvents/BlogPedidoUrgente'));

const Railway = lazy(() => import('./Pages/IndustrialSectors/Railway'));

// Campaign landing pages (standalone, no language prefix)
const Innotrans2026 = lazy(() => import('./Pages/Campaigns/Innotrans2026'));

const GroupManagement = lazy(() => import('./Pages/HansaFlexGroupPages/GroupManagement'));
const GroupCertificates = lazy(() => import('./Pages/HansaFlexGroupPages/GroupCertificates'));
const GroupHistory = lazy(() => import('./Pages/HansaFlexGroupPages/GroupHistory'));
const GroupMissionStatement = lazy(() => import('./Pages/HansaFlexGroupPages/GroupMissionStatement'));
const AnnualReport = lazy(() => import('./Pages/HansaFlexGroupPages/AnnualReport'));
const Associations = lazy(() => import('./Pages/HansaFlexGroupPages/Associations'));
const ResponsibilityAndSocialCommitments = lazy(() => import('./Pages/HansaFlexGroupPages/ResponsibilityAndSocialCommitments/ResponsibilityAndSocialCommitments'));
const DonationInsteadGifts = lazy(() => import('./Pages/HansaFlexGroupPages/ResponsibilityAndSocialCommitments/DonationInsteadGifts'));
const Diversity = lazy(() => import('./Pages/HansaFlexGroupPages/ResponsibilityAndSocialCommitments/Diversity'));

const SixtyYearOfHansaflex = lazy(() => import('./Pages/HansaFlexGroupPages/SixtyYearOfHansaFlex'));
const PrivacyPolicy = lazy(() => import('./Pages/PrivacyPolicy'));
const LegalNotice = lazy(() => import('./Pages/LegalNotice'));
const GeneralConditions = lazy(() => import('./Pages/GeneralConditions'));
const HosesAndHoseLines = lazy(() => import('./Pages/ProductPages/HosesAndHoseLines/HosesAndHoseLines'));
const HydraulicHose = lazy(() => import('./Pages/ProductPages/HosesAndHoseLines/HydraulicHose'));
const MetalHoses = lazy(() => import('./Pages/ProductPages/HosesAndHoseLines/MetalHoses'));
const IndustrialHoses = lazy(() => import('./Pages/ProductPages/HosesAndHoseLines/IndustrialHoses'));
const PerformedHoses = lazy(() => import('./Pages/ProductPages/HosesAndHoseLines/PerformedHoses'));
const ExtremelyHighPerformanceHoses = lazy(() => import('./Pages/ProductPages/HosesAndHoseLines/ExtremelyHighPerformanceHoses'));
const HoseLineConfigurator = lazy(() => import('./Pages/ProductPages/HosesAndHoseLines/HoseLineConfigurator'));
const PtfeHoses = lazy(() => import('./Pages/ProductPages/HosesAndHoseLines/PtfeHoses'));
const Accessories = lazy(() => import('./Pages/ProductPages/HosesAndHoseLines/Accessories'));
const DriveAndControlTechnology = lazy(() => import('./Pages/ProductPages/DriveAndControlTechnology/DriveAndControlTechnology'));
const Pumps = lazy(() => import('./Pages/ProductPages/DriveAndControlTechnology/Pumps'));
const HydraulicMotor = lazy(() => import('./Pages/ProductPages/DriveAndControlTechnology/HydraulicMotor'));
const Cylinder = lazy(() => import('./Pages/ProductPages/DriveAndControlTechnology/Cylinder'));
const SwitchesAndSensors = lazy(() => import('./Pages/ProductPages/DriveAndControlTechnology/SwitchesAndSensors'));
const ValveTechnology = lazy(() => import('./Pages/ProductPages/DriveAndControlTechnology/ValveTechnology/ValveTechnology'));
const PipelineInstallationValve = lazy(() => import('./Pages/ProductPages/DriveAndControlTechnology/ValveTechnology/PipelineInstallationValve'));
const CartridgeValves = lazy(() => import('./Pages/ProductPages/DriveAndControlTechnology/ValveTechnology/CartridgeValves'));
const CetopValves = lazy(() => import('./Pages/ProductPages/DriveAndControlTechnology/ValveTechnology/CetopValves'));
const MeasuringTechnology = lazy(() => import('./Pages/ProductPages/DriveAndControlTechnology/MeasuringTechnology'));
const SevenHundredBarHydraulics = lazy(() => import('./Pages/ProductPages/DriveAndControlTechnology/SevenHundredBarHydraulics'));
const PressureAccumulators = lazy(() => import('./Pages/ProductPages/DriveAndControlTechnology/PressureAccumulators'));
const SealingTechnology = lazy(() => import('./Pages/ProductPages/SealingTechnology'));
const Filtration = lazy(() => import('./Pages/ProductPages/Filtration'));
const Pneumatics = lazy(() => import('./Pages/ProductPages/Pneumatics'));
const TechnicalInformation = lazy(() => import('./Pages/ProductPages/TechnicalInformation'));
const Approvals = lazy(() => import('./Pages/ProductPages/Approvals'));
const Catalogues = lazy(() => import('./Pages/ProductPages/Catalogues'));
const HydraulicConnectionTechnology = lazy(() => import('./Pages/ProductPages/HydraulicConnectionTechnology/HydraulicConnectionTechnology'));
const HoseFittings = lazy(() => import('./Pages/ProductPages/HydraulicConnectionTechnology/HoseFittings'));
const PipeFittings = lazy(() => import('./Pages/ProductPages/HydraulicConnectionTechnology/PipeFittings'));
const Adapters = lazy(() => import('./Pages/ProductPages/HydraulicConnectionTechnology/Adapters'));
const Couplings = lazy(() => import('./Pages/ProductPages/HydraulicConnectionTechnology/Couplings'));
const BallValves = lazy(() => import('./Pages/ProductPages/HydraulicConnectionTechnology/BallValves'));
const Flanges = lazy(() => import('./Pages/ProductPages/HydraulicConnectionTechnology/Flanges'));
const Pipelines = lazy(() => import('./Pages/ProductPages/Pipelines/Pipelines'));
const Pipebends = lazy(() => import('./Pages/ProductPages/Pipelines/Pipebends'));
const ExpansionJoints = lazy(() => import('./Pages/ProductPages/Pipelines/ExpansionJoints'));
const PipeendProcessing = lazy(() => import('./Pages/ProductPages/Pipelines/PipeendProcessing'));
const HoseManagement = lazy(() => import('./Pages/ServicesPages/PreventativeMaintainance/HoseManagement/HoseManagement'));
const FluidService = lazy(() => import('./Pages/ServicesPages/FluidManagement/FluidService'));
const MobileService = lazy(() => import('./Pages/ServicesPages/MobileRapidHydraulicService'));
const HydraulicAcademy = lazy(() => import('./Pages/ServicesPages/HydraulicAccademy'));
const OilAnalysis = lazy(() => import('./Pages/ServicesPages/FluidManagement/OilAnalysis'));
const OilFilteringAndCleaning = lazy(() => import('./Pages/ServicesPages/FluidManagement/OilFilteringAndCleaning'));
const AssemblyAndInstallation = lazy(() => import('./Pages/ServicesPages/AssembellyAndInstallation/AssemblyAndInstallation'));
const ModernisationAndOptimisation = lazy(() => import('./Pages/ServicesPages/AssembellyAndInstallation/ModernisationAndOptimisation'));
const PlantProjectPlanningAndManagement = lazy(() => import('./Pages/ServicesPages/AssembellyAndInstallation/PlantProjectPlanningAndManagement'));
const InstallationOfPipeAndHoseLineSystems = lazy(() => import('./Pages/ServicesPages/AssembellyAndInstallation/InstallationOfPipeAndHoseLineSystems'));
const PreventataiveMaintainance = lazy(() => import('./Pages/ServicesPages/PreventativeMaintainance/PreventataiveMaintainance'));
const ServicePackages = lazy(() => import('./Pages/ServicesPages/PreventativeMaintainance/HoseManagement/ServicePackages'));
const XCodeIdentification = lazy(() => import('./Pages/ServicesPages/PreventativeMaintainance/HoseManagement/XCodeIdentification'));
const XCodeManager = lazy(() => import('./Pages/ServicesPages/PreventativeMaintainance/HoseManagement/XCodeManager/XCodeManager'));
const Licenses = lazy(() => import('./Pages/ServicesPages/PreventativeMaintainance/HoseManagement/XCodeManager/Licenses'));
const Inspection = lazy(() => import('./Pages/ServicesPages/PreventativeMaintainance/Inspection/Inspection'));
const IndustrialHoseInspection = lazy(() => import('./Pages/ServicesPages/PreventativeMaintainance/Inspection/IndustrialHoseInspection'));
const DguvInspection = lazy(() => import('./Pages/ServicesPages/PreventativeMaintainance/Inspection/DguvInspection'));
const LiftingPlatformsAndLoadingBridges = lazy(() => import('./Pages/ServicesPages/PreventativeMaintainance/Inspection/LiftingPlatformsAndLoadingBridges'));
const RepairAndOverhaul = lazy(() => import('./Pages/ServicesPages/RepairAndOverhaul/RepairAndOverhaul'));
const PressureAccumulatorsAndAssociatedSystems = lazy(() => import('./Pages/ServicesPages/RepairAndOverhaul/PressureAccumulatorsAndAssociatedSystems'));
const HydraulicCylinderRepair = lazy(() => import('./Pages/ServicesPages/RepairAndOverhaul/HydraulicCylinderRepair'));
const DigitalServices = lazy(() => import('./Pages/ServicesPages/Digital Services/DigitalServices'));
const EDIandBMEcat = lazy(() => import('./Pages/ServicesPages/Digital Services/EDIandBMEcat'));
const Scannersolutions = lazy(() => import('./Pages/ServicesPages/Digital Services/Scannersolutions'));
const Production = lazy(() => import('./Pages/ServicesPages/Production/Production'));
const CustomisedAssembly = lazy(() => import('./Pages/ServicesPages/Production/CustomisedAssembly'));
const SeriesProduction = lazy(() => import('./Pages/ServicesPages/Production/SeriesProduction'));
const Logisticssolutions = lazy(() => import('./Pages/ServicesPages/Logistics solutions/Logisticssolutions'));
const Warehousemanagement = lazy(() => import('./Pages/ServicesPages/Logistics solutions/Warehousemanagement'));
const Kanbandelivery = lazy(() => import('./Pages/ServicesPages/Logistics solutions/Kanbandelivery'));
const Kitting = lazy(() => import('./Pages/ServicesPages/Logistics solutions/Kitting'));
const Hydraulicworkshopcontainers = lazy(() => import('./Pages/ServicesPages/Logistics solutions/Hydraulicworkshopcontainers'));
const Worksinworks = lazy(() => import('./Pages/ServicesPages/Logistics solutions/Worksinworks'));
const Engineering = lazy(() => import('./Pages/ServicesPages/Enginnering/Engineering'));
const EngineeredbyHansaflex = lazy(() => import('./Pages/ServicesPages/Enginnering/EngineeredbyHansaflex'));
const Hydraulicunit = lazy(() => import('./Pages/ServicesPages/Enginnering/Hydraulicunit'));
const Prototypemanufacture = lazy(() => import('./Pages/ServicesPages/Enginnering/Prototypemanufacture'));
const Pressureaccumulatorinspection = lazy(() => import('./Pages/ServicesPages/Pressureaccumulatorinspection'));
const NotFound = lazy(() => import('./Pages/NotFound'));

// Component to track pageviews on route changes
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    // Track pageview when route changes
    const pagePath = location.pathname + location.search;
    const pageTitle = document.title || 'HANSA-FLEX Spain';

    // Wait a bit to ensure GTM is loaded (if consent granted)
    // Only track if GTM is available (user has consented)
    const timer = setTimeout(() => {
      const shouldTrack = window.dataLayer && (window.HFAnalyticsEnabled || window.HFConsentStatus === 'accepted');
      if (shouldTrack) {
        trackPageView(pagePath, pageTitle);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [location]);

  return null;
}

// Utilities and language-aware routing
function getDetectedOrDefaultLang() {
  const supported = ['en', 'es'];
  const current = i18n.language && i18n.language.substring(0, 2);
  if (current && supported.includes(current)) return current;
  if (typeof navigator !== 'undefined') {
    const nav = (navigator.language || navigator.userLanguage || 'en').substring(0, 2);
    if (supported.includes(nav)) return nav;
  }
  // Spanish, not English: a .es domain serving the Spanish market. This must
  // stay in step with X_DEFAULT_LANG (scripts/generate-sitemap.js) and
  // BARE_DEFAULT_LANG (scripts/generate-apache-conf.js) — if the hreflang
  // x-default tag disagrees with where the site actually sends people,
  // Google discards the whole hreflang set.
  return 'es';
}

function RedirectToDetectedLang() {
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const path = location.pathname || '/';
    const isPrefixed = /^\/(en|es)(\/|$)/i.test(path);
    if (!isPrefixed) {
      const lng = getDetectedOrDefaultLang();
      startTransition(() => {
        history.replace(`/${lng}${path.startsWith('/') ? '' : '/'}${path}` + (location.search || '') + (location.hash || ''));
      });
    }
  }, [location, history]);
  return null;
}

function LanguageRoutes() {
  const { lng } = useParams();
  const history = useHistory();
  useEffect(() => {
    const supported = ['en', 'es'];
    if (!supported.includes(lng)) {
      startTransition(() => {
        history.replace(`/en`);
      });
      return;
    }
    if (i18n.language !== lng) {
      i18n.changeLanguage(lng);
    }
  }, [lng, history]);


  function AppContent() {
    const { i18n } = useTranslation();

    return (
      <>
        {/* One component writes every head tag for every page — see
            PageMeta.jsx. Deliberately mounted here rather than in each page,
            so the 106 page components need no changes. */}
        <PageMeta />
        <PageViewTracker />
        <div className="App">
          <header className="App-header">
            <div className='primary-navbar'>
              <CustomNavbar />
            </div>
          </header>
          <div className='secondary-navbar sticky-top'>
            {i18n.language === 'es' ? <ES_MainNavbarMenu /> : <MainNavbarMenu />}
          </div>
          <MobileNavbar />
          <Suspense fallback={<div className="SpinnerForLoading" >
            <Spinner animation="grow" role="status">
              <span className=""></span>
            </Spinner>
          </div>}>

            {/* <span className="mt-2">{t('LoadingMessage')}</span> */}
            <Switch>
              <Route path="/:lng(en|es)" exact component={HomePage} />
              {/* <Route path="/products" component={ProductsPage} /> */}
              {/* <Route path="/hf-group" component={HansaFlexGroupPage} /> */}

              {/* HF Spain Pages */}
              <Route path={`/:lng(en|es)${getLocalizedSlug('who_we_are', lng)}`} exact component={WhoWeAre} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('management', lng)}`} component={Management} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('certificates', lng)}`} component={Certificates} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('careers', lng)}`} component={Careers} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('current_vacancies', lng)}`} component={CurrentVacanciew} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('contact_us', lng)}`} component={ContactUs} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('contact', lng)}`} component={Contact} />

              <Route path={`/:lng(en|es)${getLocalizedSlug('terms_and_conditions', lng)}`} component={TermsAndConditions} />


              {/* HF Group Pages */}
              <Route path={`/:lng(en|es)${getLocalizedSlug('hf_group_management', lng)}`} component={GroupManagement} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('hf_group_certificates', lng)}`} component={GroupCertificates} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('hf_group_history', lng)}`} component={GroupHistory} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('hf_group_mission', lng)}`} component={GroupMissionStatement} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('hf_60_years', lng)}`} component={SixtyYearOfHansaflex} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('annual_report', lng)}`} component={AnnualReport} />
              {/* <Route path="/upcoming-events" component={UpcomingEvents} />/ */}
              <Route path={`/:lng(en|es)${getLocalizedSlug('associations', lng)}`} component={Associations} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('responsibility_social', lng)}`} component={ResponsibilityAndSocialCommitments} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('donation_instead_gifts', lng)}`} component={DonationInsteadGifts} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('diversity', lng)}`} component={Diversity} />

              {/* Campaign landing pages. Also reachable at the bare
                  /innotrans2026 (see the top-level Switch) — that is the URL
                  printed on the InnoTrans material. Deliberately not linked
                  from any navigation menu. */}
              <Route path={`/:lng(en|es)${getLocalizedSlug('innotrans2026', lng)}`} exact component={Innotrans2026} />

              {/* News and Blogs */}
              <Route path={`/:lng(en|es)${getLocalizedSlug('blogs', lng)}`} component={NewsEventsPage} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('blog_pedido_urgente', lng)}`} component={BlogPedidoUrgente} />

              {/* Privacy Policy and Legal Notice */}
              <Route path={`/:lng(en|es)${getLocalizedSlug('privacy_policy', lng)}`} component={PrivacyPolicy} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('legal_notice', lng)}`} component={LegalNotice} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('general_conditions', lng)}`} component={GeneralConditions} />

              {/* Industrial Sectors */}
              <Route path={`/:lng(en|es)${getLocalizedSlug('railway', lng)}`} component={Railway} />

              {/* HF Products Pages */}
              {/* <Route path={`/:lng(en|es)${getLocalizedSlug('products', lng)}`} component={ProductsPage} /> */}
              <Route path={`/:lng(en|es)${getLocalizedSlug('hoses_and_hose_lines', lng)}`} component={HosesAndHoseLines} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('hose_line_config', lng)}`} component={HoseLineConfigurator} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('hydraulic_hoses', lng)}`} component={HydraulicHose} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('metal_hoses', lng)}`} component={MetalHoses} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('industrial_hoses', lng)}`} component={IndustrialHoses} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('performed_hoses', lng)}`} component={PerformedHoses} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('ext_high_pfm_hoses', lng)}`} component={ExtremelyHighPerformanceHoses} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('ptfe_hoses', lng)}`} component={PtfeHoses} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('hose_accessories', lng)}`} component={Accessories} />

              <Route path={`/:lng(en|es)${getLocalizedSlug('drive_and_control', lng)}`} component={DriveAndControlTechnology} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('pumps', lng)}`} component={Pumps} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('hydraulic_motor', lng)}`} component={HydraulicMotor} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('cylinder', lng)}`} component={Cylinder} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('switches_sensors', lng)}`} component={SwitchesAndSensors} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('valve_tech', lng)}`} component={ValveTechnology} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('pipeline_installation_valve', lng)}`} component={PipelineInstallationValve} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('cartridge_valve', lng)}`} component={CartridgeValves} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('cetop_valve', lng)}`} component={CetopValves} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('measuring_tech', lng)}`} component={MeasuringTechnology} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('seven_hundred_hydraulics', lng)}`} component={SevenHundredBarHydraulics} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('pressure_accumulators', lng)}`} component={PressureAccumulators} />

              <Route path={`/:lng(en|es)${getLocalizedSlug('hydraulic_connection_technology', lng)}`} component={HydraulicConnectionTechnology} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('hose_fitting', lng)}`} component={HoseFittings} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('pipe_fitting', lng)}`} component={PipeFittings} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('adapters', lng)}`} component={Adapters} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('coupling', lng)}`} component={Couplings} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('ball_valves', lng)}`} component={BallValves} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('flanges', lng)}`} component={Flanges} />

              <Route path={`/:lng(en|es)${getLocalizedSlug('sealing_tech', lng)}`} component={SealingTechnology} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('filtration', lng)}`} component={Filtration} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('pneumatics', lng)}`} component={Pneumatics} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('technical_information', lng)}`} component={TechnicalInformation} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('approvals', lng)}`} component={Approvals} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('catalogues', lng)}`} component={Catalogues} />

              <Route path={`/:lng(en|es)${getLocalizedSlug('pipelines', lng)}`} component={Pipelines} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('pipebends', lng)}`} component={Pipebends} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('pipeend_processing', lng)}`} component={PipeendProcessing} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('expansion_joints', lng)}`} component={ExpansionJoints} />

              {/* HF Services Pages */}
              <Route path={`/:lng(en|es)${getLocalizedSlug('hose_management', lng)}`} component={HoseManagement} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('mobile_rapid_service', lng)}`} component={MobileService} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('hydraulic_academy', lng)}`} component={HydraulicAcademy} />

              <Route path={`/:lng(en|es)${getLocalizedSlug('fluid_service', lng)}`} component={FluidService} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('oil_analysis', lng)}`} component={OilAnalysis} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('oil_filtering_cleaning', lng)}`} component={OilFilteringAndCleaning} />

              <Route path={`/:lng(en|es)${getLocalizedSlug('assembly_installation', lng)}`} component={AssemblyAndInstallation} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('modernisation_optimisation', lng)}`} component={ModernisationAndOptimisation} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('plant_project_planning', lng)}`} component={PlantProjectPlanningAndManagement} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('installation_of_pipe_hoseline', lng)}`} component={InstallationOfPipeAndHoseLineSystems} />

              <Route path={`/:lng(en|es)${getLocalizedSlug('preventative_maintainance', lng)}`} component={PreventataiveMaintainance} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('service_packages', lng)}`} component={ServicePackages} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('xcode', lng)}`} component={XCodeIdentification} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('xcode_manager', lng)}`} component={XCodeManager} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('licenses', lng)}`} component={Licenses} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('inspection', lng)}`} component={Inspection} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('industrial_hose_inspection', lng)}`} component={IndustrialHoseInspection} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('dguv_inspection', lng)}`} component={DguvInspection} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('lifting_platforms_loading_bridges', lng)}`} component={LiftingPlatformsAndLoadingBridges} />

              <Route path={`/:lng(en|es)${getLocalizedSlug('repair_and_overhaul', lng)}`} component={RepairAndOverhaul} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('hydraulic_cylinder_repair', lng)}`} component={HydraulicCylinderRepair} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('pressure_accumulators_systems', lng)}`} component={PressureAccumulatorsAndAssociatedSystems} />

              <Route path={`/:lng(en|es)${getLocalizedSlug('digital_services', lng)}`} component={DigitalServices} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('edi_bmecat', lng)}`} component={EDIandBMEcat} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('scanner_solutions', lng)}`} component={Scannersolutions} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('production', lng)}`} component={Production} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('customised_assembly', lng)}`} component={CustomisedAssembly} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('series_production', lng)}`} component={SeriesProduction} />

              <Route path={`/:lng(en|es)${getLocalizedSlug('logistics_solutions', lng)}`} component={Logisticssolutions} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('warehouse_management', lng)}`} component={Warehousemanagement} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('kanban_delivery', lng)}`} component={Kanbandelivery} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('kitting', lng)}`} component={Kitting} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('hydraulic_workshop_containers', lng)}`} component={Hydraulicworkshopcontainers} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('works_in_works', lng)}`} component={Worksinworks} />

              <Route path={`/:lng(en|es)${getLocalizedSlug('engineering', lng)}`} component={Engineering} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('engineered_by_hansa_flex', lng)}`} component={EngineeredbyHansaflex} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('hydraulic_unit', lng)}`} component={Hydraulicunit} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('prototype_manufacture', lng)}`} component={Prototypemanufacture} />
              <Route path={`/:lng(en|es)${getLocalizedSlug('pressure_accumulator_inspection', lng)}`} component={Pressureaccumulatorinspection} />

              {/* MUST stay last. Switch renders the first match, so anything
                  added below this line would never be reached. Catches every
                  unknown path under /en or /es — including unprefixed URLs,
                  which RedirectToDetectedLang sends here with a prefix. */}
              <Route path="*" component={NotFound} />
            </Switch>
          </Suspense>
          <div>
            <HfFooter />
          </div>
          {/* <WhatsAppChat /> */}
          {/* <ExitIntentPopup /> */}
          <CookieBanner />
        </div>
      </>
    );
  }

  return <AppContent />;
}

export default function App() {
  return (
    <Router>
      <Suspense fallback={
        <div className="SpinnerForLoading" >
          <Spinner animation="grow" role="status">
            <span className=""></span>
          </Spinner>
        </div>}>
        <Switch>
          <Route path="/:lng(en|es)">
            <LanguageRoutes />
          </Route>
          <Route path="*">
            <RedirectToDetectedLang />
          </Route>
        </Switch>
      </Suspense>
    </Router>
  );
}
